import logger from '@eventbrite/client-logger';
import {
    getLocaleOverride,
    setupAsync,
    getParentLocale,
} from '@eventbrite/i18n';
import { initializeErrorReporting } from '@eventbrite/error-reporting';
import {
    initDimensionsFromState,
    trackInitialPageView,
} from '@eventbrite/site-analytics';
import { loadableReady } from '@loadable/component';
import {
    Dedupe as DedupeIntegration,
    ExtraErrorData as ExtraErrorDataIntegration,
    HttpClient as HttpClientIntegration,
} from '@sentry/integrations';
import React from 'react';
import { hydrate, render } from 'react-dom';
import { initializeStatsigSdk } from './actions/statsig';
import { initializeSplitioSdk } from './actions/splitio';
import { checkoutBFFBaseUrlForEnvironment } from './api/constants';
import App from './app';
import GenericErrorMessage from './components/errorNotification/GenericErrorMessage';
import { initDatadogRUM } from './initDatadogRUM';
import { createCheckoutStore } from './utils/store';
import { refreshAndAppendVersionParamIfRedirectedFromExternalPaymentAndBranchTesting } from './utils/externalPaymentRedirect';
import { getIsAppRebrandActiveFromURL } from './utils/getIsAppRebrandActiveFromURL';
import { initializeAppRebrandFromParams } from './utils/initializeAppRebrandFromParams';
import { shouldIgnoreStatsigError } from './utils/shouldIgnoreStatsigError';

import { popSession } from './session';

const root = document.getElementById('root');
const props = window.__SERVER_DATA__;
const isProd = (domain = {}) => domain.includes('eventbrite');
const isAppRebrandActive = getIsAppRebrandActiveFromURL();

const SENTRY_PROD_DSN =
    'https://63c4a0ad4bca496d8b1b036af895d74e@o375540.ingest.us.sentry.io/6141609';

const SENTRY_QA_DSN =
    'https://634079eca45440fea24efb5df085bf9a@o375540.ingest.us.sentry.io/6141609';

const getSentryDSN = (isProd) => (isProd ? SENTRY_PROD_DSN : SENTRY_QA_DSN);

const main = async () => {
    if (!props) {
        throw new Error('props is missing');
    }
    if (!root) {
        throw new Error('root is null');
    }

    // Fix for EB-314568
    const cookies = document.cookie.split(';').map((s) => s.trim());
    const occurrences = cookies.filter((s) =>
        s.startsWith('csrftoken='),
    ).length;

    if (occurrences > 1) {
        // Delete csrftoken for www.eventbrite.com
        document.cookie = `csrftoken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; SameSite=Lax`;
    }
    // End fix for  EB-314568

    // __LOCALS__ is defined only when running checkout using `bolt dev`. In this situation __SERVER_DATA__ does not have app_name nor app_version
    const appName =
        props.app_name ??
        window.__LOCALS__?.application?.name
            .replace('@eventbrite/', '')
            .replace('@eventbrite/', '');
    const appVersion = props.app_version;

    const parentLocale = getParentLocale(
        getLocaleOverride() || props.env.localeInfo.locale,
    );

    // Load and clear any previously saved Redux state.
    // We remove it immediately after loading because we want to restore the
    // application state only once.
    const initialState = popSession();

    await refreshAndAppendVersionParamIfRedirectedFromExternalPaymentAndBranchTesting(
        initialState,
        isProd,
    );
    const { store, initialPath, history } = createCheckoutStore(
        {
            ...props,
            parentLocale,
        },
        initialState,
    );

    const isAppRebrandActiveFromInitialState = initializeAppRebrandFromParams(
        initialState,
        store.dispatch,
    );

    if (isAppRebrandActive || isAppRebrandActiveFromInitialState) {
        import('./loadRebrandingFont').then((module) => {
            module.loadRebrandingFont();
        });
    }

    try {
        store.dispatch(initializeSplitioSdk());

        initializeErrorReporting({
            dsn: getSentryDSN(isProd(props.env.ebDomain)),
            metaData: {
                tags: {
                    event_id: props.event.id,
                },
            },
            integrations: [
                new DedupeIntegration(),
                new ExtraErrorDataIntegration(),
                new HttpClientIntegration({
                    failedRequestTargets: [
                        ...Object.values(checkoutBFFBaseUrlForEnvironment),
                    ],
                }),
            ],
            ignoreError: shouldIgnoreStatsigError,
        });

        await initializeStatsigSdk(store.getState(), props.stableId || null);

        if (parentLocale !== 'en_US') {
            await setupAsync({
                parentLocale,
                translations:
                    /* webpackChunkName: "checkout-i18n-translation" */ import(
                        `./i18n/translations/${parentLocale}.json`
                    ),
            });
        }
    } finally {
        // render app with server props to the DOM
        loadableReady(() => {
            const appNode = (
                <App
                    {...props}
                    appName={appName}
                    appVersion={appVersion}
                    initialPath={initialPath}
                    store={store}
                    history={history}
                />
            );

            // SSR process does not have access to the initialState because it
            // is stored in the browser sessionStorage, rather than a cookie so
            // if we are initializing the app from a stored state we must ignore
            // the SSR results and render over again. Otherwise we will run into
            // hydration mismatches that React solves wrongly
            if (initialState) {
                root.innerHTML = '';

                render(appNode, root);
            } else {
                hydrate(appNode, root);
            }
        });

        initDimensionsFromState(props);
        trackInitialPageView(props.gaSettings);
        if (isProd(props.env.ebDomain)) {
            initDatadogRUM({
                app: { name: appName, version: appVersion },
            });
        }
    }
};

try {
    main();
} catch (error) {
    logger.error('checkoutWidget main error:', {
        error: error.message ? error.message : error,
        props,
    });
    if (root) {
        try {
            render(
                <GenericErrorMessage isAppRebrandActive={isAppRebrandActive} />,
                root,
            );
        } catch (e) {
            logger.error('GenericErrorMessage error:', {
                error: e,
            });
        }
    }
}
